import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Building2, CreditCard, ShieldCheck, Save, Loader2, Shield, Camera, Trash2 } from "lucide-react";
import DateField from "@/components/app/DateField";

const API = process.env.REACT_APP_BACKEND_URL;

const COUNTRIES = [
  { code: "ES", name: "España" },
  { code: "PT", name: "Portugal" },
  { code: "FR", name: "Francia" },
  { code: "AD", name: "Andorra" },
  { code: "IT", name: "Italia" },
  { code: "DE", name: "Alemania" },
];

function Field({ label, value, onChange, testid, placeholder, type = "text", required, colSpan = 1, hint }) {
  return (
    <div className={`col-span-${colSpan}`}>
      <label className="overline block mb-1">{label} {required && <span className="text-primary">*</span>}</label>
      <input
        data-testid={testid}
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        className="w-full h-10 bg-transparent border border-border px-3 font-mono text-sm focus:border-primary focus:outline-none"
      />
      {hint && <div className="text-[10px] text-muted-foreground mt-1">{hint}</div>}
    </div>
  );
}

function LogoUploader({ club, onUpdated }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [bust, setBust] = useState(0);
  const src = club?.logo_url
    ? `${API}${club.logo_url.startsWith("/") ? "" : "/"}${club.logo_url}${bust ? (club.logo_url.includes("?") ? "&" : "?") + "cb=" + bust : ""}`
    : null;

  const handleFile = async (f) => {
    if (!f || !club?.id) return;
    if (!f.type.startsWith("image/")) { toast.error("Solo imágenes"); return; }
    if (f.size > 2 * 1024 * 1024) { toast.error("Máximo 2 MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post(`/clubs/${club.id}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBust(Date.now());
      onUpdated?.(data.logo_url);
      toast.success("Escudo actualizado");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error al subir");
    } finally { setUploading(false); }
  };

  const handleDelete = async () => {
    if (!club?.id || !window.confirm("¿Eliminar el escudo del club?")) return;
    try {
      await api.delete(`/clubs/${club.id}/logo`);
      onUpdated?.(null);
      toast.success("Escudo eliminado");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error");
    }
  };

  return (
    <div className="flex items-center gap-5" data-testid="settings-logo-uploader">
      <div className="relative flex-shrink-0">
        <div className="w-28 h-28 border-2 border-border flex items-center justify-center bg-white overflow-hidden">
          {src ? (
            <img src={src} alt={club?.name || "Escudo"} className="w-full h-full object-contain p-1" />
          ) : (
            <Shield size={48} className="text-slate-400" />
          )}
        </div>
        {uploading && <div className="absolute inset-0 bg-black/70 flex items-center justify-center text-[10px] uppercase font-heading tracking-wider">Subiendo…</div>}
      </div>
      <div className="flex flex-col gap-2">
        <input ref={inputRef} type="file" accept="image/*" className="hidden"
               onChange={(e) => handleFile(e.target.files?.[0])} data-testid="settings-logo-input" />
        <button type="button" data-testid="settings-logo-upload-btn" onClick={() => inputRef.current?.click()} disabled={uploading}
                className="inline-flex items-center gap-2 h-9 px-4 bg-primary text-primary-foreground text-xs uppercase font-heading tracking-widest hover:bg-primary/80 disabled:opacity-50">
          <Camera size={12}/> {src ? "Cambiar escudo" : "Subir escudo"}
        </button>
        {src && (
          <button type="button" data-testid="settings-logo-delete-btn" onClick={handleDelete}
                  className="inline-flex items-center gap-2 h-9 px-4 border border-red-500/40 text-red-400 text-xs uppercase font-heading tracking-widest hover:bg-red-500/10">
            <Trash2 size={12}/> Eliminar
          </button>
        )}
        <div className="text-[10px] text-muted-foreground max-w-xs">Preferiblemente PNG con fondo transparente. Se ve mejor si la imagen es cuadrada.</div>
      </div>
    </div>
  );
}

export default function ClubSettings() {
  const [club, setClub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [ibanInput, setIbanInput] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/clubs/me/settings");
      setClub(data);
      if (data.iban_last4) setIbanInput(`•••• •••• •••• ${data.iban_last4}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const update = (k, v) => setClub((c) => ({ ...c, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: club.name,
        city: club.city,
        contact_email: club.contact_email,
        contact_phone: club.contact_phone,
        fiscal_name: club.fiscal_name,
        tax_id: club.tax_id,
        address_line: club.address_line,
        postal_code: club.postal_code,
        country: club.country,
        billing_email: club.billing_email,
        billing_phone: club.billing_phone,
        federation_number: club.federation_number,
        lopd_consent: club.lopd_consent,
      };
      // Only send iban if the user typed a fresh full IBAN (not the masked view)
      if (ibanInput && !ibanInput.startsWith("••••")) {
        payload.iban_last4 = ibanInput.replace(/\s/g, "");
      }
      const { data } = await api.put("/clubs/me/settings", payload);
      setClub(data);
      if (data.iban_last4) setIbanInput(`•••• •••• •••• ${data.iban_last4}`);
      toast.success("Datos del club guardados");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !club) {
    return <div className="p-10 flex items-center gap-3 text-sm text-muted-foreground"><Loader2 className="animate-spin" size={18}/> Cargando…</div>;
  }

  return (
    <div className="max-w-4xl">
      <div className="mb-8">
        <div className="overline">Configuración</div>
        <h1 className="font-heading font-black text-4xl md:text-5xl tracking-tighter uppercase mt-2">Datos del club</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          Mantén estos datos actualizados. Se utilizan para las facturas de Stripe, para las comunicaciones oficiales
          con las familias y para las declaraciones federativas. Solo el administrador del club puede editarlos.
        </p>
      </div>

      <form onSubmit={save} className="space-y-6">
        {/* Escudo / Logo */}
        <section className="border-2 border-white/10 p-6" data-testid="settings-section-logo">
          <div className="flex items-center gap-2 mb-4">
            <Shield size={16} className="text-primary"/>
            <div className="overline text-primary">Escudo del club</div>
          </div>
          <p className="text-xs text-muted-foreground mb-4">
            El escudo aparecerá en toda la app, en los PDFs del jugador, en los emails de convocatoria y en la ficha pública. Formato JPG o PNG, máximo 2 MB. Se recomienda una imagen cuadrada.
          </p>
          <LogoUploader club={club} onUpdated={(url) => setClub((c) => ({ ...c, logo_url: url }))} />
        </section>

        {/* Datos generales */}
        <section className="border-2 border-white/10 p-6" data-testid="settings-section-general">
          <div className="flex items-center gap-2 mb-4">
            <Building2 size={16} className="text-primary"/>
            <div className="overline text-primary">Datos generales</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Nombre del club" value={club.name} onChange={(v) => update("name", v)} testid="cs-name" required placeholder="Club Deportivo Ejemplo"/>
            <Field label="Ciudad" value={club.city} onChange={(v) => update("city", v)} testid="cs-city" placeholder="Madrid"/>
            <Field label="Email de contacto" type="email" value={club.contact_email} onChange={(v) => update("contact_email", v)} testid="cs-email" placeholder="contacto@ejemplo.com"/>
            <Field label="Teléfono de contacto" value={club.contact_phone} onChange={(v) => update("contact_phone", v)} testid="cs-phone" placeholder="+34 600 000 000"/>
            <Field label="Nº federado (opcional)" value={club.federation_number} onChange={(v) => update("federation_number", v)} testid="cs-fed" placeholder="12345" colSpan={2}/>
          </div>
        </section>

        {/* Datos fiscales */}
        <section className="border-2 border-white/10 p-6" data-testid="settings-section-fiscal">
          <div className="flex items-center gap-2 mb-4">
            <ShieldCheck size={16} className="text-primary"/>
            <div className="overline text-primary">Datos fiscales</div>
          </div>
          <p className="text-xs text-muted-foreground mb-4">Aparecerán en las facturas emitidas por Sudeporte y en las que tú emitas a las familias desde la plataforma.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Razón social" value={club.fiscal_name} onChange={(v) => update("fiscal_name", v)} testid="cs-fiscal-name" placeholder="Club Deportivo Ejemplo SL"/>
            <Field label="NIF / CIF" value={club.tax_id} onChange={(v) => update("tax_id", v)} testid="cs-tax-id" placeholder="B12345678"/>
            <Field label="Dirección" value={club.address_line} onChange={(v) => update("address_line", v)} testid="cs-address" placeholder="C/ Mayor 12, 3º B" colSpan={2}/>
            <Field label="Código postal" value={club.postal_code} onChange={(v) => update("postal_code", v)} testid="cs-cp" placeholder="28001"/>
            <div>
              <label className="overline block mb-1">País</label>
              <select data-testid="cs-country" value={club.country || "ES"} onChange={(e) => update("country", e.target.value)}
                      className="w-full h-10 bg-transparent border border-border px-3 font-mono text-sm focus:border-primary focus:outline-none">
                {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
              </select>
            </div>
            <Field label="Email de facturación" type="email" value={club.billing_email} onChange={(v) => update("billing_email", v)} testid="cs-billing-email" placeholder="facturacion@ejemplo.com" hint="Si es distinto del email de contacto"/>
            <Field label="Teléfono de facturación" value={club.billing_phone} onChange={(v) => update("billing_phone", v)} testid="cs-billing-phone"/>
          </div>
        </section>

        {/* Datos bancarios */}
        <section className="border-2 border-white/10 p-6" data-testid="settings-section-bank">
          <div className="flex items-center gap-2 mb-4">
            <CreditCard size={16} className="text-primary"/>
            <div className="overline text-primary">Domiciliación bancaria (opcional)</div>
          </div>
          <p className="text-xs text-muted-foreground mb-4">
            Si quieres pagar por <b>domiciliación SEPA</b> en lugar de tarjeta, guarda aquí tu IBAN.
            El primer cobro se realizará desde <b>Sudefootball · Facturación → Suscribirse</b> donde te
            pediremos firmar el mandato SEPA (obligatorio por normativa europea). <b>Solo guardamos los últimos 4 dígitos</b> por seguridad.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field
              label="IBAN"
              value={ibanInput}
              onChange={(v) => setIbanInput(v.toUpperCase())}
              testid="cs-iban"
              placeholder="ES91 2100 0418 4502 0005 1332"
              hint={club.iban_last4 ? `IBAN registrado terminado en ${club.iban_last4}. Deja el campo tal cual para no cambiarlo.` : "Pega tu IBAN completo. Guardaremos solo los últimos 4 dígitos."}
              colSpan={2}
            />
          </div>
        </section>

        {/* Consentimientos */}
        <section className="border-2 border-white/10 p-6" data-testid="settings-section-lopd">
          <label className="flex items-start gap-3 text-sm cursor-pointer">
            <input type="checkbox" data-testid="cs-lopd" checked={!!club.lopd_consent} onChange={(e) => update("lopd_consent", e.target.checked)} className="mt-1 accent-primary"/>
            <span className="text-slate-300">
              Confirmo que el club dispone del consentimiento LOPD/GDPR de los jugadores y familias cuyos datos se registrarán en Sudefootball, y que puedo aportar dichos consentimientos a requerimiento de la autoridad competente.
            </span>
          </label>
        </section>

        <div className="flex justify-end">
          <button data-testid="cs-save-btn" type="submit" disabled={saving} className="btn-volt disabled:opacity-60">
            {saving ? <Loader2 className="animate-spin" size={14}/> : <Save size={14}/>}
            {saving ? "Guardando…" : "Guardar cambios"}
          </button>
        </div>
      </form>

      {/* Seasons management — decoupled from the club settings form */}
      <SeasonsSection />
    </div>
  );
}

function SeasonsSection() {
  const [seasons, setSeasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ label: "", start_date: "", end_date: "", is_active: false });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/seasons");
      setSeasons(data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    const year = new Date().getFullYear();
    setEditing(null);
    setForm({ label: `${year}-${year + 1}`, start_date: `${year}-08-01`, end_date: `${year + 1}-07-31`, is_active: seasons.length === 0 });
    setShowForm(true);
  };
  const openEdit = (s) => {
    setEditing(s);
    setForm({ label: s.label, start_date: s.start_date, end_date: s.end_date, is_active: !!s.is_active });
    setShowForm(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/seasons/${editing.id}`, form);
        toast.success("Temporada actualizada");
      } else {
        await api.post("/seasons", form);
        toast.success("Temporada creada");
      }
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const del = async (s) => {
    if (!window.confirm(`¿Eliminar la temporada ${s.label}? Los datos históricos NO se borran.`)) return;
    try {
      await api.delete(`/seasons/${s.id}`);
      toast.success("Temporada eliminada");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <section className="border-2 border-white/10 p-6 mt-8" data-testid="settings-section-seasons">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="overline text-primary">Temporadas</div>
          <h2 className="font-heading font-black uppercase text-2xl tracking-tight mt-1">Temporadas del club</h2>
          <p className="text-xs text-muted-foreground mt-2 max-w-2xl">
            Registra las temporadas del club con nombre y rango de fechas. Sirven para agrupar estadísticas y comparativas. Marca una como activa para que sea la seleccionada por defecto en dropdowns.
          </p>
        </div>
        <button data-testid="seasons-new-btn" onClick={openCreate}
          className="h-10 px-4 bg-primary text-white hover:bg-primary/90 text-xs uppercase font-heading tracking-wider">+ Nueva temporada</button>
      </div>

      {loading ? (
        <div className="text-muted-foreground text-sm">Cargando…</div>
      ) : seasons.length === 0 ? (
        <div className="border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          Aún no has definido ninguna temporada. Crea la primera con el botón de arriba.
        </div>
      ) : (
        <div className="border border-border overflow-x-auto">
          <table className="w-full text-sm" data-testid="seasons-table">
            <thead className="bg-card">
              <tr className="text-[10px] uppercase tracking-wider text-muted-foreground">
                <th className="text-left py-2 px-3 font-heading">Etiqueta</th>
                <th className="text-left py-2 px-3 font-heading">Inicio</th>
                <th className="text-left py-2 px-3 font-heading">Fin</th>
                <th className="text-left py-2 px-3 font-heading">Activa</th>
                <th className="text-right py-2 px-3 font-heading">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {seasons.map(s => (
                <tr key={s.id} data-testid={`season-row-${s.id}`} className="border-t border-border hover:bg-white/[0.02]">
                  <td className="py-2 px-3 font-heading font-semibold">{s.label}</td>
                  <td className="py-2 px-3 text-xs">{s.start_date}</td>
                  <td className="py-2 px-3 text-xs">{s.end_date}</td>
                  <td className="py-2 px-3">
                    {s.is_active
                      ? <span className="inline-block px-2 py-0.5 border border-emerald-500/50 text-emerald-400 text-[10px] uppercase tracking-wider">Activa</span>
                      : <span className="text-muted-foreground text-xs">—</span>}
                  </td>
                  <td className="py-2 px-3">
                    <div className="flex justify-end gap-2">
                      <button data-testid={`season-edit-${s.id}`} onClick={() => openEdit(s)}
                        className="h-8 px-3 border border-border hover:border-primary hover:text-primary text-[10px] uppercase font-heading tracking-wider transition-colors">Editar</button>
                      <button data-testid={`season-delete-${s.id}`} onClick={() => del(s)}
                        className="h-8 px-3 border border-border hover:border-destructive hover:text-destructive text-[10px] uppercase font-heading tracking-wider transition-colors">Eliminar</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="season-modal">
          <form onSubmit={save} className="bg-card border border-border w-full max-w-md p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="overline text-primary">{editing ? "Editar" : "Nueva"} temporada</div>
                <h3 className="font-heading font-black uppercase text-xl mt-1">{editing ? editing.label : "Nueva temporada"}</h3>
              </div>
              <button type="button" onClick={() => setShowForm(false)} className="text-muted-foreground hover:text-white" data-testid="season-modal-close">✕</button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="overline block mb-1">Etiqueta *</label>
                <input data-testid="season-form-label" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
                  placeholder="2025-2026" required
                  className="w-full h-10 bg-transparent border border-border px-3 font-mono text-sm focus:border-primary focus:outline-none" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="overline block mb-1">Fecha inicio *</label>
                  <DateField testid="season-form-start" value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v })} />
                </div>
                <div>
                  <label className="overline block mb-1">Fecha fin *</label>
                  <DateField testid="season-form-end" value={form.end_date} onChange={(v) => setForm({ ...form, end_date: v })} />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input data-testid="season-form-active" type="checkbox" checked={!!form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="accent-primary" />
                Marcar como temporada activa
              </label>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setShowForm(false)} className="h-10 px-4 border border-border hover:border-primary hover:text-primary text-xs uppercase font-heading tracking-wider">Cancelar</button>
              <button type="submit" data-testid="season-form-save" className="h-10 px-5 bg-primary text-white hover:bg-primary/90 text-xs uppercase font-heading tracking-wider">Guardar</button>
            </div>
          </form>
        </div>
      )}
    </section>
  );
}
